import React, { useState, useEffect, useCallback } from 'react';
import { useMoney } from '../hooks/useMoney';
import { ParsedVendor, VendorSentiment, VendorSignalSource } from '../data/vendorData';
import { apiGet, planningPath } from '../services/PlanningApiClient';
import Icon from './Icon';
import ContentCard from './ContentCard';
import '../styles/components/VendorRecommendations.css';
import EmptyState from './EmptyState';
import LoadErrorBox from './ui/LoadErrorBox';

interface ApiRecommendRow {
  vendor_id: string;
  name: string;
  category: string | null;
  price_range: string;
  rating: number;
  review_count: number;
  verified: boolean;
  instagram: string | null;
  whatsapp: string | null;
  phone: string | null;
  sentiment: VendorSentiment | null;
  sentiment_note: string | null;
  signal_source: VendorSignalSource | null;
  tags: string[] | null;
  quote_amount: number | null;
  notes: string | null;
  relevance_score: number;
}

interface RecommendResponse {
  vendors: ApiRecommendRow[];
  meta: { category: string; exclude: string | null; total: number };
}

interface VendorRecommendationsProps {
  vendor: ParsedVendor;
  onVendorSelect?: (vendorId: string) => void;
}

const SIGNAL_LABEL: Record<VendorSignalSource, string> = {
  planner_recommended: 'Planner pick',
  family_shared: 'Family endorsed',
  quoted:         'Quoted',
  referred:       'Referred',
  scraped:        'Discovered',
};

const SIGNAL_BADGE_TYPE: Record<VendorSignalSource, 'success' | 'warning' | 'danger' | 'info'> = {
  planner_recommended: 'success',
  family_shared:       'warning',
  quoted:              'info',
  referred:            'info',
  scraped:             'info',
};

const SENTIMENT_DOT: Record<VendorSentiment, string> = {
  positive:  '#10b981',
  neutral:   '#f59e0b',
  negative:  '#ef4444',
  undecided: '#9ca3af',
};

const VendorRecommendations: React.FC<VendorRecommendationsProps> = ({ vendor, onVendorSelect }) => {
  const money = useMoney();
  const [recs, setRecs] = useState<ApiRecommendRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const category = vendor.category ?? vendor.services[0] ?? '';

  const load = useCallback(async () => {
    if (!category) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ category, exclude: vendor.id, limit: '5' });
      if (vendor.priceRange) params.set('price_range', vendor.priceRange);
      const res = await apiGet<RecommendResponse>(
        planningPath(`/vendors/recommend?${params}`)
      );
      setRecs(res.vendors ?? []);
    } catch (e) {
      setError(e ?? new Error('load failed'));
    } finally {
      setLoading(false);
    }
  }, [vendor.id, vendor.priceRange, category]);

  useEffect(() => { load(); }, [load]);

  if (!category) return null;

  return (
    <section className="side-section vr-root">
      <div className="side-section-header">
        <h4>
          <Icon name="users" size={14} />
          Alternatives
        </h4>
        <span className="vr-subtitle">{category}</span>
      </div>

      {loading && (
        <div className="vr-loading">
          <div className="loading-spinner-sm" />
          Finding similar vendors…
        </div>
      )}

      {error != null && <LoadErrorBox what="similar vendors" error={error} onRetry={load} retrying={loading} />}

      {!loading && error == null && recs.length === 0 && (
        <EmptyState compact title="No similar vendors in database yet" />
      )}

      {!loading && recs.map(r => {
        const signal = (r.signal_source ?? 'scraped') as VendorSignalSource;
        const sentiment = (r.sentiment ?? 'undecided') as VendorSentiment;
        const instagramHandle = r.instagram?.replace(/^@/, '') ?? '';
        const igUrl = instagramHandle ? `https://instagram.com/${instagramHandle}` : null;

        const details = [
          { label: 'Price range', value: r.price_range },
          ...(r.rating > 0 ? [{ label: 'Rating', value: r.rating.toFixed(1) }] : []),
        ];

        const tags = (r.tags ?? [])
          .slice(0, 4)
          .map(t => (typeof t === 'string' ? t : (t as { name?: string }).name ?? ''))
          .filter(Boolean);

        const actions = [
          ...(igUrl
            ? [{ icon: 'instagram', title: 'Instagram', onClick: () => window.open(igUrl, '_blank', 'noopener,noreferrer') }]
            : []),
          ...(r.whatsapp || r.phone
            ? [{
                icon: 'message-circle',
                title: 'WhatsApp',
                onClick: () => window.open(`https://wa.me/${(r.whatsapp ?? r.phone ?? '').replace(/\D/g, '')}`, '_blank', 'noopener,noreferrer'),
              }]
            : []),
        ];

        return (
          <ContentCard
            key={r.vendor_id}
            hideImage
            title={r.name}
            sentiment={{ color: SENTIMENT_DOT[sentiment], label: sentiment }}
            badges={signal !== 'scraped' ? [{ label: SIGNAL_LABEL[signal], type: SIGNAL_BADGE_TYPE[signal] }] : undefined}
            details={details}
            pricing={r.quote_amount ? { amount: r.quote_amount, currency: money.symbol, note: 'Quoted' } : undefined}
            description={r.sentiment_note ?? undefined}
            tags={tags}
            actions={actions.length > 0 ? actions : undefined}
            onClick={onVendorSelect ? () => onVendorSelect(r.vendor_id) : undefined}
          />
        );
      })}
    </section>
  );
};

export default VendorRecommendations;
